import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Loader2, ScanSearch } from 'lucide-react';
import { useFind, type FindResult } from '@/api/insights';
import { useScopeFilter } from '@/contexts/ClientScopeContext';
import { FIND_ICON, FIND_LABEL, FIND_ORDER, FindTags, findPath, rankForScope } from '@/components/insights/FindKit';
import { insightsFindPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

const IS_MAC = typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform);
const PER_TYPE = 5;

/**
 * Header record search ("Find", Ctrl/⌘ Shift F) — devices, people (M365 users + Autotask
 * contacts), tickets by number/id and companies (server/insights/find.ts). Deliberately separate
 * from the page search beside it (decision 2026-10-13). Enter on nothing selected, or "See all",
 * opens /insights/find.
 */
export function RecordFind() {
  const navigate = useNavigate();
  const { companyId } = useScopeFilter();
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => { const t = setTimeout(() => setTerm(q), 250); return () => clearTimeout(t); }, [q]);
  const { data, isFetching } = useFind(term);

  // Grouped by type, a few each, in a fixed order; the flat list drives arrow keys.
  const groups = useMemo(() => {
    const ranked = rankForScope(data?.results ?? [], companyId);
    return FIND_ORDER.map((type) => ({ type, items: ranked.filter((r) => r.type === type).slice(0, PER_TYPE) })).filter((g) => g.items.length);
  }, [data, companyId]);
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  useEffect(() => { setCursor(0); }, [term]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        input.current?.focus();
        input.current?.select();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const go = (to: string) => { navigate(to); setQ(''); setTerm(''); setOpen(false); input.current?.blur(); };
  const pick = (r: FindResult) => go(findPath(r));
  const showAll = () => q.trim().length >= 2 && go(insightsFindPath(q.trim()));
  const short = q.trim().length < 2;
  const settled = term === q && !isFetching;

  return (
    <div ref={box} className="relative hidden md:block">
      <ScanSearch className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <input
        ref={input}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, flat.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); if (flat[cursor] && term === q) pick(flat[cursor]); else showAll(); }
          else if (e.key === 'Escape') { setQ(''); setOpen(false); input.current?.blur(); }
        }}
        placeholder="Find device, person, ticket…"
        aria-label="Find a device, person, ticket or company"
        role="combobox"
        aria-expanded={open && !short}
        aria-controls="record-find-results"
        className="h-8 w-48 rounded-md border border-border bg-background pl-8 pr-3 text-sm text-foreground outline-none transition-[width,box-shadow] placeholder:text-muted-foreground focus:w-72 focus:border-ring focus:ring-2 focus:ring-ring/30 lg:w-[19.5rem] lg:pr-[4.5rem] lg:focus:w-96"
      />
      <kbd className="pointer-events-none absolute right-2 hidden lg:block top-1/2 -translate-y-1/2 rounded border border-border bg-muted px-1 text-[10px] text-muted-foreground">
        {IS_MAC ? '⇧⌘F' : 'Ctrl ⇧ F'}
      </kbd>

      {open && !short && (
        <div id="record-find-results" role="listbox"
          className="absolute right-0 z-50 mt-1.5 w-[440px] overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg">
          {flat.length === 0 ? (
            <div className="flex items-center justify-center gap-2 px-4 py-6 text-center text-xs text-muted-foreground">
              {!settled ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching every module…</> : <>Nothing matches “{q.trim()}”.</>}
            </div>
          ) : (
            <div className="max-h-[65vh] overflow-y-auto py-1">
              {groups.map((g) => (
                <div key={g.type}>
                  <div className="px-3 pb-0.5 pt-2 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{FIND_LABEL[g.type]}</div>
                  {g.items.map((r) => {
                    const i = flat.indexOf(r);
                    const Icon = FIND_ICON[r.type];
                    return (
                      <button key={`${r.type}:${r.key}`} type="button" role="option" aria-selected={i === cursor}
                        onMouseEnter={() => setCursor(i)} onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                        className={cn('flex w-full items-center gap-3 px-3 py-1.5 text-left text-sm', i === cursor ? 'bg-muted' : 'hover:bg-muted/50')}>
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand"><Icon className="h-3.5 w-3.5" /></span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="truncate font-medium text-foreground">{r.title}</span>
                            <FindTags tags={r.tags.slice(0, 3)} />
                          </span>
                          <span className="block truncate text-[11px] text-muted-foreground">{[r.companyName, r.subtitle].filter(Boolean).join(' · ')}</span>
                        </span>
                        {i === cursor && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
          <button type="button" onMouseDown={(e) => { e.preventDefault(); showAll(); }}
            className="flex w-full items-center justify-between border-t border-divider px-3 py-2 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground">
            <span>See all results for “{q.trim()}”{data && settled ? ` (${data.results.length})` : ''}</span>
            {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
          </button>
        </div>
      )}
    </div>
  );
}
